/**
 * SCM8 — vendor quote PDF extractor.
 *
 * Uses the three-tier extraction architecture FA13 introduced in
 * `extractPpaTerms.ts` (since removed as dormant — restore from 9cb2c600c if a
 * PPA term-sheet surface is built), applied to a different input shape — vendor quote PDFs from common
 * energy-equipment templates (Hitachi Energy, GE Vernova, Siemens
 * Energy, NuScale, Prysmian, Tesla Megapack, Cummins).
 *
 *   Tier 1 — defined-language regex patterns (high confidence)
 *   Tier 2 — section-heading routing (medium confidence)
 *   Tier 3 — LLM-assisted on whatever's still missing (low confidence)
 *
 * Each tier writes only into fields that earlier tiers missed, so a
 * tier-1 hit can never be overwritten by tier-2 / 3. The text is
 * provided pre-extracted (caller wires `extractPdfTextFromBytes` from
 * `services/knowledge/documentUpload`).
 *
 * Output: a partial `ExtractedQuote` plus per-field provenance.
 * The caller maps the extracted fields onto a `Quote` record using
 * `quoteFromExtracted()` (provided below) which fills in the
 * structural fields the extractor doesn't see (bomItemId, supplierId,
 * documentId, status, expiresAt, etc.).
 *
 * Bidirectional link discipline:
 *   - `document.linkedBomItemIds` — list of BoM items the quote covers
 *   - `quote.documentId` — back-pointer to the document
 *   - `quote.bomItemId` — forward pointer to the BoM item
 *   - `bomItem.priceProvenance.quoteId` — provenance back to the quote
 *
 * TODO(orchestrator): SCM-Data will land the canonical
 * `src/types/supplyChain.ts`. The local stub matches the spec shapes
 * so the swap is mechanical at merge time.
 */
import type { FieldProvenance } from '../../types/financialAssumptions';
import type { Quote } from '../../types/supplyChain';
import {
  QUOTE_DEFINED_PATTERNS,
  QUOTE_SECTION_ROUTES,
  QUOTE_STRING_PATTERNS,
} from './quotePatterns';

/**
 * The structured subset of a `Quote` that comes from PDF parsing.
 * The remaining structural fields (`id`, `bomItemId`, `supplierId`,
 * `receivedAt`, `expiresAt`, `status`, `documentId`) are supplied by
 * the caller — the extractor doesn't know which BoM item / supplier
 * the quote belongs to. The user multi-selects against the current
 * site's BoM after the extractor returns.
 */
export interface ExtractedQuote {
  unitCostUsd: number | null;
  leadTimeMonths: number | null;
  validityDays: number | null;
  paymentTerms: string | null;
  freightTerms: string | null;
  currency: string | null;
}

export type ExtractedQuoteField = keyof ExtractedQuote;

export const EXTRACTED_QUOTE_FIELDS: ExtractedQuoteField[] = [
  'unitCostUsd',
  'leadTimeMonths',
  'validityDays',
  'paymentTerms',
  'freightTerms',
  'currency',
];

export interface QuoteExtractionSummary {
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  missing: number;
}

export interface QuoteExtractionResult {
  fields: Partial<ExtractedQuote>;
  fieldProvenance: Partial<Record<ExtractedQuoteField, FieldProvenance>>;
  warnings: string[];
  summary: QuoteExtractionSummary;
}

/**
 * Optional LLM tier-3 extractor signature. Caller-injectable so the
 * pure extractor stays test-friendly and offline by default.
 */
export type QuoteLlmExtractor = (
  text: string,
  missingFields: ExtractedQuoteField[],
) => Promise<{
  values: Partial<ExtractedQuote>;
  cellReferences: Partial<Record<ExtractedQuoteField, string>>;
}>;

export interface ExtractQuoteOptions {
  llmExtractor?: QuoteLlmExtractor;
  /** Override the timestamp baked into provenance (test stability). */
  now?: () => string;
}

/**
 * Pure extraction over a quote PDF's pre-extracted plain text.
 */
export async function extractQuoteFromPdf(
  text: string,
  options: ExtractQuoteOptions = {},
): Promise<QuoteExtractionResult> {
  const now = options.now ?? ((): string => new Date().toISOString());
  const fields: Partial<ExtractedQuote> = {};
  const provenance: Partial<Record<ExtractedQuoteField, FieldProvenance>> = {};
  const warnings: string[] = [];

  // ── Tier 1 — defined-language patterns (numeric fields) ──────────
  for (const def of QUOTE_DEFINED_PATTERNS) {
    if (fields[def.field] !== undefined) continue;
    const match = def.pattern.exec(text);
    if (!match) continue;
    const raw = match[def.group];
    if (raw == null) continue;
    const value = def.transform ? def.transform(raw) : raw;
    if (value == null) continue;
    (fields as Record<string, unknown>)[def.field] = value;
    provenance[def.field] = {
      source: 'extracted-defined-pattern',
      confidence: 'high',
      cellReference: cellRefForOffset(text, match.index),
      extractedAt: now(),
    };
  }

  // ── Tier 1 — string-valued patterns ──────────────────────────────
  for (const sp of QUOTE_STRING_PATTERNS) {
    if (fields[sp.field] !== undefined) continue;
    const match = sp.pattern.exec(text);
    if (!match) continue;
    const raw = match[sp.group];
    if (raw == null) continue;
    const value = sp.transform ? sp.transform(raw) : raw.trim();
    if (value == null) continue;
    (fields as Record<string, unknown>)[sp.field] = value;
    provenance[sp.field] = {
      source: 'extracted-defined-pattern',
      confidence: 'high',
      cellReference: cellRefForOffset(text, match.index),
      extractedAt: now(),
    };
  }

  // ── Tier 2 — section routing ─────────────────────────────────────
  for (const route of QUOTE_SECTION_ROUTES) {
    if (fields[route.field] !== undefined) continue;
    const headingMatch = route.heading.exec(text);
    if (!headingMatch) continue;
    const sectionStart = headingMatch.index + headingMatch[0].length;
    // Scope the body search to ~1200 chars after the heading or up to
    // the next "Section/Article" — typical clause width in vendor quotes.
    const nextHeading = /(?:section|article|§|chapter)\s+(?:[IVX]+|\d+)/i.exec(
      text.slice(sectionStart),
    );
    const sectionEnd = nextHeading
      ? sectionStart + nextHeading.index
      : Math.min(text.length, sectionStart + 1200);
    const section = text.slice(sectionStart, sectionEnd);
    const bodyMatch = route.body.exec(section);
    if (!bodyMatch) continue;
    const raw = bodyMatch[route.group];
    if (raw == null) continue;
    const value = route.transform ? route.transform(raw) : raw;
    if (value == null) continue;
    (fields as Record<string, unknown>)[route.field] = value;
    provenance[route.field] = {
      source: 'extracted-section',
      confidence: 'medium',
      cellReference: cellRefForOffset(text, sectionStart + bodyMatch.index),
      extractedAt: now(),
    };
  }

  // Multiple price callouts → surface a warning so reviewers can verify.
  const priceMatches = text.match(/\$\s*[0-9][0-9,]*(?:\.\d{1,2})?/g);
  if (priceMatches && priceMatches.length > 1 && fields.unitCostUsd != null) {
    warnings.push(
      `Found ${priceMatches.length} $ amounts — used the first parsed unit/total price. Review if a different line item applies.`,
    );
  }

  // ── Tier 3 — LLM fallback ────────────────────────────────────────
  const stillMissing = EXTRACTED_QUOTE_FIELDS.filter(
    (f) => fields[f] === undefined,
  );
  if (options.llmExtractor && stillMissing.length > 0) {
    try {
      const llm = await options.llmExtractor(text, stillMissing);
      for (const field of stillMissing) {
        const v = llm.values[field];
        if (v === undefined || v === null) continue;
        (fields as Record<string, unknown>)[field] = v;
        provenance[field] = {
          source: 'extracted-llm',
          confidence: 'low',
          cellReference: llm.cellReferences[field],
          extractedAt: now(),
        };
      }
    } catch (err) {
      warnings.push(
        `LLM extraction failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  return {
    fields,
    fieldProvenance: provenance,
    warnings,
    summary: summarize(provenance),
  };
}

function summarize(
  provenance: Partial<Record<ExtractedQuoteField, FieldProvenance>>,
): QuoteExtractionSummary {
  let high = 0;
  let med = 0;
  let low = 0;
  for (const field of EXTRACTED_QUOTE_FIELDS) {
    const p = provenance[field];
    if (!p) continue;
    if (p.confidence === 'high') high += 1;
    else if (p.confidence === 'medium') med += 1;
    else low += 1;
  }
  return {
    highConfidence: high,
    mediumConfidence: med,
    lowConfidence: low,
    missing: EXTRACTED_QUOTE_FIELDS.length - high - med - low,
  };
}

/**
 * Pin the structural fields the extractor doesn't see — `bomItemId`,
 * `supplierId`, `documentId`, etc. — onto an extracted quote to produce
 * a complete `Quote` record ready to be appended to
 * `SupplyChainPlan.quotes`.
 *
 * Bidirectional linkage is the caller's responsibility:
 *   1. Push the returned `Quote` to `plan.quotes`.
 *   2. Set `document.linkedBomItemIds = [...existing, bomItemId]`
 *      so the document carries the back-pointer (SCM8 spec).
 *   3. Optionally update `bomItem.priceProvenance.quoteId = quote.id`
 *      so the BoM row's price chip cites the quote.
 *
 * Defaults applied here:
 *   - `status` = 'open' (caller can override for re-imported award)
 *   - `unitCostUsd` = 0 when the extractor returned null (caller
 *     should validate before persisting — a zero-priced quote almost
 *     always means a parse miss, not a free vendor)
 *   - `leadTimeMonths` = 0 with the same caveat
 *   - `expiresAt` defaults to receivedAt + validityDays * 86400 ms
 *     when the extractor pulled a validity period; otherwise
 *     receivedAt + 30 days as a conservative default.
 */
export function quoteFromExtracted(
  extracted: Partial<ExtractedQuote>,
  spec: {
    id: string;
    bomItemId: string;
    supplierId: string;
    documentId?: string;
    receivedAt: string;
    notes?: string;
    /** Overrides for fields the extractor didn't see. */
    overrides?: Partial<Pick<Quote, 'status' | 'unitCostUsd' | 'leadTimeMonths' | 'expiresAt'>>;
  },
): Quote {
  const validityMs =
    (extracted.validityDays != null ? extracted.validityDays : 30) *
    24 *
    60 *
    60 *
    1000;
  const receivedMs = Date.parse(spec.receivedAt);
  const expiresAt =
    spec.overrides?.expiresAt
    ?? new Date(receivedMs + validityMs).toISOString();
  return {
    id: spec.id,
    bomItemId: spec.bomItemId,
    supplierId: spec.supplierId,
    documentId: spec.documentId,
    receivedAt: spec.receivedAt,
    expiresAt,
    status: spec.overrides?.status ?? 'open',
    unitCostUsd: spec.overrides?.unitCostUsd ?? extracted.unitCostUsd ?? 0,
    leadTimeMonths:
      spec.overrides?.leadTimeMonths ?? extracted.leadTimeMonths ?? 0,
    validityDays: extracted.validityDays ?? undefined,
    paymentTerms: extracted.paymentTerms ?? undefined,
    freightTerms: extracted.freightTerms ?? undefined,
    currency: extracted.currency ?? undefined,
    notes: spec.notes,
  };
}

/**
 * Approximate "page" / section reference from a character offset.
 * Falls back to `char @ offset` when no `\f` page markers or
 * `Section X` headings are present. Mirrors the FA13 helper.
 */
function cellRefForOffset(text: string, offset: number): string {
  const before = text.slice(0, offset);
  const pageBreaks = before.match(/\f/g);
  if (pageBreaks && pageBreaks.length > 0) {
    return `p.${pageBreaks.length + 1}`;
  }
  const sectionRe = /(?:section|article|§)\s+([IVX]+|\d+)/gi;
  let lastSection: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = sectionRe.exec(before)) !== null) {
    lastSection = m[1];
  }
  if (lastSection) return `§${lastSection}`;
  return `char @ ${offset}`;
}

export const __test__ = { summarize, cellRefForOffset };
